import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Layers, Plus, Trash2, Users, Pencil, Shield, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StaffSearchSelect from "@/components/admin/StaffSearchSelect";
import { TimetableAdminSkeleton } from "@/components/skeletons/PageSkeletons";

const TABS = [
  { id: "timetable", label: "Timetable", icon: Calendar },
  { id: "classes", label: "Class offerings", icon: Layers },
  { id: "staff", label: "Staff assignments", icon: Users },
];

function fmtTime(t) {
  if (!t) return "";
  return String(t).slice(0, 5);
}

function slotKey(s) {
  return `${s.start_time}|${s.end_time}`;
}

const emptySlotForm = () => ({
  section_id: "",
  course_id: "",
  class_id: "",
  period_key: "",
  classroom_id: "",
  staff_qids: [],
});

export default function TimetableAdmin() {
  const [tab, setTab] = useState("timetable");
  const [meta, setMeta] = useState(null);
  const [slots, setSlots] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [sectionId, setSectionId] = useState("all");
  const [activeDay, setActiveDay] = useState("Mon");

  const [slotDialog, setSlotDialog] = useState(null);
  const [slotForm, setSlotForm] = useState(emptySlotForm());
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [newClass, setNewClass] = useState({ course_id: "", section_id: "" });
  const [newAssignment, setNewAssignment] = useState({ staff_qid: "", class_id: "" });
  const [staffSearch, setStaffSearch] = useState("");

  const loadMeta = useCallback(async () => {
    const { data } = await api.get("/admin/meta");
    setMeta(data);
    return data;
  }, []);

  const loadSlots = useCallback(async (sectionFilter) => {
    const params = {};
    if (sectionFilter && sectionFilter !== "all") params.section_id = sectionFilter;
    const { data } = await api.get("/admin/timetable", { params });
    setSlots(data);
  }, []);

  const loadAssignments = useCallback(async () => {
    const { data } = await api.get("/admin/staff-assignments");
    setAssignments(data);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await loadMeta();
      await Promise.all([loadSlots(sectionId), loadAssignments()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [loadMeta, loadSlots, loadAssignments, sectionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!loading) loadSlots(sectionId);
  }, [sectionId, loadSlots, loading]);

  const daySlots = useMemo(
    () => slots.filter((s) => s.day === activeDay && (sectionId === "all" || String(s.section_id) === String(sectionId))),
    [slots, activeDay, sectionId]
  );

  const slotByPeriod = useMemo(() => {
    const map = new Map();
    for (const s of daySlots) map.set(slotKey(s), s);
    return map;
  }, [daySlots]);

  const assignmentsByClassId = useMemo(() => {
    const map = new Map();
    for (const a of assignments) {
      const key = String(a.class_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return map;
  }, [assignments]);

  const staffNamesForClass = useCallback(
    (classId) => {
      const list = assignmentsByClassId.get(String(classId)) || [];
      return list.map((a) => a.staff_name).join(", ") || "—";
    },
    [assignmentsByClassId]
  );

  const staffQidsForClass = useCallback(
    (classId) => (assignmentsByClassId.get(String(classId)) || []).map((a) => a.staff_qid),
    [assignmentsByClassId]
  );

  const filteredAssignments = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return assignments;
    return assignments.filter(
      (a) =>
        (a.staff_name || "").toLowerCase().includes(q) ||
        (a.staff_qid || "").toLowerCase().includes(q) ||
        (a.label || "").toLowerCase().includes(q)
    );
  }, [assignments, staffSearch]);

  const classesForSection = useCallback(
    (secId) => (meta?.classes || []).filter((c) => String(c.section_id) === String(secId)),
    [meta]
  );

  const resolveClassId = async (sectionIdVal, courseIdVal) => {
    const existing = classesForSection(sectionIdVal).find((c) => String(c.course_id) === String(courseIdVal));
    if (existing) return existing.id;
    const { data } = await api.post("/admin/classes", {
      course_id: Number(courseIdVal),
      section_id: Number(sectionIdVal),
    });
    await loadMeta();
    return data.id;
  };

  const syncStaffForClass = async (classId, staffQids) => {
    const existing = assignmentsByClassId.get(String(classId)) || [];
    const target = new Set(staffQids);

    for (const row of existing) {
      if (!target.has(row.staff_qid)) {
        await api.delete(`/admin/staff-assignments/${row.id}`);
      }
    }
    for (const qid of staffQids) {
      if (!existing.some((row) => row.staff_qid === qid)) {
        await api.post("/admin/staff-assignments", {
          staff_qid: qid,
          class_id: Number(classId),
        });
      }
    }
    await loadAssignments();
  };

  const courseStaffQids = (sectionIdVal, courseIdVal) => {
    const cls = classesForSection(sectionIdVal).find((c) => String(c.course_id) === String(courseIdVal));
    return cls ? staffQidsForClass(cls.id) : [];
  };

  const openCreateSlot = (period) => {
    setSlotForm({
      ...emptySlotForm(),
      section_id: sectionId !== "all" ? String(sectionId) : "",
      period_key: slotKey(period),
    });
    setSlotDialog({ mode: "create", period });
  };

  const openEditSlot = (slot) => {
    const period = meta?.period_slots?.find((p) => slotKey(p) === slotKey(slot));
    setSlotForm({
      section_id: String(slot.section_id ?? ""),
      course_id: String(slot.course_id ?? ""),
      class_id: String(slot.class_id ?? ""),
      period_key: slotKey(slot),
      classroom_id: slot.classroom_id ? String(slot.classroom_id) : "",
      staff_qids: staffQidsForClass(slot.class_id),
    });
    setSlotDialog({ mode: "edit", slot, period });
  };

  const saveSlot = async () => {
    const period = meta?.period_slots?.find((p) => slotKey(p) === slotForm.period_key);
    if (!slotForm.section_id || !slotForm.course_id || !period) {
      toast.error("Select section, course, and period");
      return;
    }
    setSaving(true);
    try {
      const classId = await resolveClassId(slotForm.section_id, slotForm.course_id);
      const body = {
        class_id: classId,
        day: activeDay,
        start_time: period.start_time,
        end_time: period.end_time,
        classroom_id: slotForm.classroom_id ? Number(slotForm.classroom_id) : null,
      };
      if (slotDialog?.mode === "edit") {
        await api.patch(`/admin/timetable/${slotDialog.slot.id}`, body);
      } else {
        await api.post("/admin/timetable", body);
      }
      await syncStaffForClass(classId, slotForm.staff_qids);
      toast.success(slotDialog?.mode === "edit" ? "Slot updated" : "Slot created");
      setSlotDialog(null);
      await loadSlots(sectionId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save slot");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      if (deleteTarget.type === "slot") {
        await api.delete(`/admin/timetable/${deleteTarget.id}`);
        toast.success("Slot deleted");
        await loadSlots(sectionId);
      } else if (deleteTarget.type === "class") {
        await api.delete(`/admin/classes/${deleteTarget.id}`);
        toast.success("Class offering removed");
        await refresh();
      } else if (deleteTarget.type === "assignment") {
        await api.delete(`/admin/staff-assignments/${deleteTarget.id}`);
        toast.success("Assignment removed");
        await loadAssignments();
      }
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const createClassOffering = async () => {
    if (!newClass.course_id || !newClass.section_id) {
      toast.error("Select course and section");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/classes", {
        course_id: Number(newClass.course_id),
        section_id: Number(newClass.section_id),
      });
      toast.success(data.created ? "Class offering created" : "Class offering already exists");
      setNewClass({ course_id: "", section_id: "" });
      await loadMeta();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create class");
    } finally {
      setSaving(false);
    }
  };

  const createAssignment = async () => {
    if (!newAssignment.staff_qid || !newAssignment.class_id) {
      toast.error("Select staff and class");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/staff-assignments", {
        staff_qid: newAssignment.staff_qid,
        class_id: Number(newAssignment.class_id),
      });
      toast.success("Staff assigned");
      setNewAssignment({ staff_qid: "", class_id: "" });
      await loadAssignments();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not assign staff");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta) {
    return <TimetableAdminSkeleton />;
  }

  return (
    <div className="min-h-screen" data-testid="timetable-admin">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
          <Shield className="w-3.5 h-3.5" />
          Super admin · Source of truth
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2">
          Timetable admin.
        </h1>
        <p className="text-[#475569] mt-3 max-w-3xl">
          Manage periods, class offerings, and staff access. Changes are validated server-side (conflicts, hall double-booking, session locks).
        </p>
      </div>

      <div className="px-6 md:px-12 border-b border-[#E2E8F0]">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                tab === id ? "border-[#C8102E] text-[#C8102E]" : "border-transparent text-[#475569]"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "timetable" && (
        <div className="px-6 md:px-12 py-8">
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              type="button"
              onClick={() => setSectionId("all")}
              className={`px-4 py-2 rounded-full text-xs font-semibold ${
                sectionId === "all" ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569]"
              }`}
            >
              All sections
            </button>
            {meta.sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSectionId(String(s.id))}
                className={`px-4 py-2 rounded-full text-xs font-semibold ${
                  String(sectionId) === String(s.id)
                    ? "bg-[#C8102E] text-white"
                    : "bg-white border border-[#E2E8F0] text-[#475569]"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          <div className="flex gap-1 overflow-x-auto border-b border-[#E2E8F0] mb-6">
            {meta.days.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDay(d)}
                className={`px-5 py-2 text-sm font-semibold border-b-2 ${
                  activeDay === d ? "border-[#C8102E] text-[#C8102E]" : "border-transparent text-[#475569]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <th className="text-left px-4 py-3 font-semibold text-[#475569] w-36">Period</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#475569]">Scheduled class</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#475569] w-48">Hall</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#475569] w-44">Staff</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#475569] w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {meta.period_slots.map((period) => {
                  const slot = slotByPeriod.get(slotKey(period));
                  return (
                    <tr key={period.label} className="border-b border-[#E2E8F0] last:border-0">
                      <td className="px-4 py-4 align-top">
                        <div className="font-semibold text-[#0F172A]">{period.label}</div>
                        <div className="text-xs text-[#94A3B8] mt-0.5">
                          {fmtTime(period.start_time)} – {fmtTime(period.end_time)}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        {slot ? (
                          <div>
                            <div className="font-semibold text-[#0F172A]">{slot.course_name}</div>
                            <div className="text-xs text-[#94A3B8]">{slot.section_name}</div>
                          </div>
                        ) : (
                          <span className="text-[#94A3B8] italic">Empty</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-[#475569]">
                        {slot?.classroom_name || "—"}
                      </td>
                      <td className="px-4 py-4 align-top text-[#475569] text-xs">
                        {slot ? staffNamesForClass(slot.class_id) : "—"}
                      </td>
                      <td className="px-4 py-4 align-top text-right">
                        {slot ? (
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditSlot(slot)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#C8102E]"
                              onClick={() => setDeleteTarget({ type: "slot", id: slot.id })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => openCreateSlot(period)}>
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {sectionId === "all" && (
            <p className="text-xs text-[#94A3B8] mt-4">
              Tip: pick a single section to avoid overlapping slots in the same period row.
            </p>
          )}
        </div>
      )}

      {tab === "classes" && (
        <div className="px-6 md:px-12 py-8">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6">
            <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-4">New class offering</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Section</Label>
                <Select value={newClass.section_id} onValueChange={(v) => setNewClass((f) => ({ ...f, section_id: v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select section" /></SelectTrigger>
                  <SelectContent>
                    {meta.sections.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course</Label>
                <Select value={newClass.course_id} onValueChange={(v) => setNewClass((f) => ({ ...f, course_id: v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {meta.courses.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name} ({c.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createClassOffering} disabled={saving} className="bg-[#C8102E] hover:bg-[#9F0D24]">
                <Plus className="w-4 h-4 mr-2" />
                Create offering
              </Button>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <th className="text-left px-4 py-3">Course</th>
                  <th className="text-left px-4 py-3">Section</th>
                  <th className="text-left px-4 py-3">Staff</th>
                  <th className="text-right px-4 py-3 w-24">Remove</th>
                </tr>
              </thead>
              <tbody>
                {meta.classes.map((c) => (
                  <tr key={c.id} className="border-b border-[#E2E8F0] last:border-0">
                    <td className="px-4 py-3">{c.course_name}</td>
                    <td className="px-4 py-3">{c.section_name}</td>
                    <td className="px-4 py-3 text-xs text-[#475569]">{staffNamesForClass(c.id)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#C8102E]"
                        onClick={() => setDeleteTarget({ type: "class", id: c.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "staff" && (
        <div className="px-6 md:px-12 py-8">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6">
            <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-4">Assign staff to class</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Staff</Label>
                <StaffSearchSelect
                  staff={meta.staff}
                  value={newAssignment.staff_qid}
                  onValueChange={(qid) => setNewAssignment((f) => ({ ...f, staff_qid: qid }))}
                  triggerLabel="Search staff by name or QID"
                />
              </div>
              <div>
                <Label>Class offering</Label>
                <Select
                  value={newAssignment.class_id}
                  onValueChange={(v) => setNewAssignment((f) => ({ ...f, class_id: v }))}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {meta.classes.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createAssignment} disabled={saving} className="bg-[#C8102E] hover:bg-[#9F0D24]">
                <Plus className="w-4 h-4 mr-2" />
                Assign
              </Button>
            </div>
          </div>

          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              placeholder="Search assignments by staff name…"
              className="pl-9 h-10 rounded-xl border-[#E2E8F0]"
              data-testid="staff-assignments-search"
            />
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <th className="text-left px-4 py-3">Staff</th>
                  <th className="text-left px-4 py-3">Class</th>
                  <th className="text-right px-4 py-3 w-24">Remove</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map((a) => (
                  <tr key={a.id} className="border-b border-[#E2E8F0] last:border-0">
                    <td className="px-4 py-3">{a.staff_name}</td>
                    <td className="px-4 py-3">{a.label}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#C8102E]"
                        onClick={() => setDeleteTarget({ type: "assignment", id: a.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!slotDialog} onOpenChange={(open) => !open && setSlotDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{slotDialog?.mode === "edit" ? "Edit period" : "Add period"} — {activeDay}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Section</Label>
              <Select
                value={slotForm.section_id}
                onValueChange={(v) =>
                  setSlotForm((f) => ({
                    ...f,
                    section_id: v,
                    course_id: "",
                    staff_qids: [],
                  }))
                }
              >
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Section" /></SelectTrigger>
                <SelectContent>
                  {meta.sections.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Course</Label>
              <Select
                value={slotForm.course_id}
                onValueChange={(v) =>
                  setSlotForm((f) => ({
                    ...f,
                    course_id: v,
                    staff_qids: courseStaffQids(f.section_id, v),
                  }))
                }
                disabled={!slotForm.section_id}
              >
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Course" /></SelectTrigger>
                <SelectContent>
                  {(slotForm.section_id ? meta.courses : []).map((c) => {
                    const hasOffering = classesForSection(slotForm.section_id).some(
                      (cl) => String(cl.course_id) === String(c.id)
                    );
                    return (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}{!hasOffering ? " (new offering)" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Period</Label>
              <Select
                value={slotForm.period_key}
                onValueChange={(v) => setSlotForm((f) => ({ ...f, period_key: v }))}
              >
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Period" /></SelectTrigger>
                <SelectContent>
                  {meta.period_slots.map((p) => (
                    <SelectItem key={p.label} value={slotKey(p)}>
                      {p.label} ({fmtTime(p.start_time)} – {fmtTime(p.end_time)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned staff</Label>
              <p className="text-xs text-[#94A3B8] mt-1 mb-2">
                Staff assigned here can view this class in their timetable and run roll-call.
              </p>
              <div className="flex flex-wrap gap-2 min-h-[32px] mb-2">
                {slotForm.staff_qids.length ? (
                  slotForm.staff_qids.map((qid) => {
                    const person = meta.staff.find((s) => s.qid === qid);
                    return (
                      <span
                        key={qid}
                        className="inline-flex items-center gap-1 rounded-full bg-[#FDECEF] px-2.5 py-1 text-xs font-medium text-[#C8102E]"
                      >
                        {person?.name || qid}
                        <button
                          type="button"
                          className="rounded-full hover:bg-[#C8102E]/10 p-0.5"
                          onClick={() =>
                            setSlotForm((f) => ({
                              ...f,
                              staff_qids: f.staff_qids.filter((id) => id !== qid),
                            }))
                          }
                          aria-label={`Remove ${person?.name || qid}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-xs text-[#94A3B8] italic">No staff assigned yet</span>
                )}
              </div>
              <StaffSearchSelect
                staff={meta.staff}
                excludeQids={slotForm.staff_qids}
                onSelect={(qid) => {
                  if (!qid || slotForm.staff_qids.includes(qid)) return;
                  setSlotForm((f) => ({ ...f, staff_qids: [...f.staff_qids, qid] }));
                }}
                triggerLabel="Add staff member"
                placeholder="Search staff by name or QID…"
                disabled={!slotForm.section_id || !slotForm.course_id}
              />
            </div>
            <div>
              <Label>Hall (required for roll-call)</Label>
              <Select
                value={slotForm.classroom_id || "none"}
                onValueChange={(v) => setSlotForm((f) => ({ ...f, classroom_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Hall" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No hall</SelectItem>
                  {meta.classrooms.map((h) => (
                    <SelectItem key={h.id} value={String(h.id)}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotDialog(null)}>Cancel</Button>
            <Button onClick={saveSlot} disabled={saving} className="bg-[#C8102E] hover:bg-[#9F0D24]">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm delete</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Slots linked to attendance sessions cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#C8102E] hover:bg-[#9F0D24]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
